<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.structure.title"
                    :subtitle="pageSubtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <template v-if="canManage">
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        @click="openCreate('sector')"
                    >
                        <v-icon class="me-1" size="small">mdi-plus</v-icon>
                        {{ t.structure.newSector }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="tonal"
                        rounded="lg"
                        class="text-none"
                        @click="openCreate('role')"
                    >
                        <v-icon class="me-1" size="small">mdi-plus</v-icon>
                        {{ t.structure.newRole }}
                    </v-btn>
                </template>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <section class="corporate-kpi-grid" :aria-label="t.common.summary">
                <article class="corporate-kpi corporate-structure-kpi">
                    <div class="corporate-structure-kpi__icon corporate-structure-kpi__icon--sector">
                        <fa icon="fa-diagram-project" />
                    </div>
                    <div>
                        <strong class="corporate-kpi__value">{{ counts.sectors }}</strong>
                        <span class="corporate-kpi__label">{{ t.structure.statsSectors }}</span>
                    </div>
                </article>
                <article class="corporate-kpi corporate-structure-kpi">
                    <div class="corporate-structure-kpi__icon corporate-structure-kpi__icon--role">
                        <fa icon="fa-id-badge" />
                    </div>
                    <div>
                        <strong class="corporate-kpi__value">{{ counts.roles }}</strong>
                        <span class="corporate-kpi__label">{{ t.structure.statsRoles }}</span>
                    </div>
                </article>
                <article
                    v-if="supportsManager"
                    class="corporate-kpi corporate-structure-kpi"
                >
                    <div class="corporate-structure-kpi__icon">
                        <fa icon="fa-user-tie" />
                    </div>
                    <div>
                        <strong class="corporate-kpi__value">{{ counts.withManager }}</strong>
                        <span class="corporate-kpi__label">{{ t.structure.statsResponsibles }}</span>
                    </div>
                </article>
                <article class="corporate-kpi corporate-structure-kpi">
                    <div class="corporate-structure-kpi__icon">
                        <fa icon="fa-circle-check" />
                    </div>
                    <div>
                        <strong class="corporate-kpi__value">{{ counts.active }}</strong>
                        <span class="corporate-kpi__label">{{ t.structure.statsActive }}</span>
                    </div>
                </article>
            </section>

            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar">
                    <v-row dense align="end">
                        <v-col cols="12" md="5">
                            <v-text-field
                                v-model="search"
                                :label="t.structure.search"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                                append-inner-icon="mdi-magnify"
                                class="inputd weeduc-admin-v-field"
                            />
                        </v-col>
                        <v-col cols="12" md="7">
                            <div class="weeduc-admin-page__toolbar-field-row">
                                <v-select
                                    v-model="typeFilter"
                                    :items="typeFilterOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.structure.filterType"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    class="inputd weeduc-admin-v-field"
                                />
                                <v-btn-toggle
                                    v-model="viewMode"
                                    mandatory
                                    density="compact"
                                    color="primary"
                                    class="corporate-structure-toolbar__toggle"
                                    @update:modelValue="onViewModeChange"
                                >
                                    <v-btn value="tree" class="text-none" size="small">
                                        <v-icon size="small" class="me-1">mdi-file-tree</v-icon>
                                        {{ t.structure.tree }}
                                    </v-btn>
                                    <v-btn value="list" class="text-none" size="small">
                                        <v-icon size="small" class="me-1">mdi-format-list-bulleted</v-icon>
                                        {{ t.structure.list }}
                                    </v-btn>
                                </v-btn-toggle>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <div class="weeduc-admin-page__table-section">
                <div v-if="loading" class="corporate-placeholder">
                    <Loader :isActive="false" />
                </div>
                <div v-else-if="error" class="corporate-placeholder">
                    {{ t.common.error }}
                </div>

                <template v-else-if="viewMode === 'tree'">
                    <div v-if="!filteredTree.length" class="corporate-placeholder">
                        {{ t.structure.empty }}
                    </div>
                    <OrgTree
                        v-else
                        :nodes="filteredTree"
                        :parent-id="null"
                        :labels="treeLabels"
                        :can-manage="canManage"
                        :show-manager="supportsManager"
                        :expanded-ids="expandedIds"
                        @edit="openEdit"
                        @remove="confirmRemove"
                        @toggle-status="toggleStatus"
                        @add-child="onAddChild"
                        @toggle-expand="toggleExpand"
                        @reorder="onReorder"
                        @open="openUnitDetail"
                    />
                </template>

                <div v-else-if="loadingList" class="corporate-placeholder">
                    <Loader :isActive="false" />
                </div>
                <div v-else-if="!filteredList.length" class="corporate-placeholder">
                    {{ t.structure.empty }}
                </div>
                <div v-else class="corporate-structure-table-wrap">
                    <v-table class="custom-table">
                        <thead>
                            <tr>
                                <th>{{ t.structure.name }}</th>
                                <th>{{ t.structure.type }}</th>
                                <th>{{ t.structure.parent }}</th>
                                <th v-if="supportsManager">{{ t.structure.manager }}</th>
                                <th class="text-center">{{ t.structure.users }}</th>
                                <th class="text-center">{{ t.structure.courses }}</th>
                                <th class="text-center">{{ t.structure.status }}</th>
                                <th class="text-center">{{ t.common.actions }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in filteredList" :key="item.id">
                                <td>
                                    <div class="corporate-structure-list-name">
                                        <span
                                            class="corporate-structure-list-badge"
                                            :class="`corporate-structure-list-badge--${item.type}`"
                                        >
                                            {{ t.structure.types[item.type] || item.type }}
                                        </span>
                                        <button
                                            v-if="item.type === 'sector' || item.type === 'role'"
                                            type="button"
                                            class="corporate-structure-list-link"
                                            @click="openUnitDetail(item)"
                                        >
                                            {{ item.name }}
                                        </button>
                                        <strong v-else>{{ item.name }}</strong>
                                    </div>
                                    <small v-if="item.description" class="corporate-structure-list-desc">
                                        {{ item.description }}
                                    </small>
                                </td>
                                <td>{{ t.structure.types[item.type] || item.type }}</td>
                                <td>{{ parentName(item.parentId) }}</td>
                                <td v-if="supportsManager">{{ item.managerName || '—' }}</td>
                                <td class="text-center">{{ item.usersCount || 0 }}</td>
                                <td class="text-center">{{ item.coursesCount || 0 }}</td>
                                <td class="text-center">
                                    <span
                                        class="corporate-structure-status"
                                        :class="item.status === 'active' ? 'is-on' : 'is-off'"
                                    >
                                        {{ item.status === 'active' ? t.structure.active : t.structure.inactive }}
                                    </span>
                                </td>
                                <td class="text-center">
                                    <template v-if="canManage">
                                        <v-btn
                                            icon
                                            size="small"
                                            variant="text"
                                            :title="t.structure.edit"
                                            :aria-label="t.structure.edit"
                                            @click="openEdit(item)"
                                        >
                                            <v-icon size="small">mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn
                                            v-if="item.type === 'role'"
                                            icon
                                            size="small"
                                            variant="text"
                                            :title="t.structure.move"
                                            :aria-label="t.structure.move"
                                            @click="openMove(item)"
                                        >
                                            <v-icon size="small">mdi-folder-move-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-btn
                                        v-if="item.type === 'sector' || item.type === 'role'"
                                        icon
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        :title="t.structure.openDetail"
                                        :aria-label="t.structure.openDetail"
                                        @click="openUnitDetail(item)"
                                    >
                                        <v-icon size="small">mdi-chart-bar</v-icon>
                                    </v-btn>
                                    <template v-if="canManage">
                                        <v-btn
                                            v-if="item.type !== 'company'"
                                            icon
                                            size="small"
                                            variant="text"
                                            color="error"
                                            :title="t.structure.delete"
                                            :aria-label="t.structure.delete"
                                            @click="confirmRemove(item)"
                                        >
                                            <v-icon size="small">mdi-delete-outline</v-icon>
                                        </v-btn>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>
                </div>
            </div>
        </div>

        <OrgUnitForm
            v-model="formOpen"
            :title="formTitle"
            :labels="t.structure"
            :common="t.common"
            :initial="formInitial"
            :parent-options="parentOptions"
            :manager-options="managerOptions"
            :managers-loading="managersLoading"
            :show-manager="supportsManager"
            :lock-type="lockType"
            :saving="saving"
            @submit="saveUnit"
            @validation-error="onFormValidationError"
        />

        <v-dialog v-model="moveOpen" max-width="520">
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">{{ t.structure.moveTitle }}</h2>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.common.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <v-select
                                v-if="moveTarget?.type === 'role'"
                                v-model="moveParentId"
                                :items="moveParentOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.structure.parent"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <p v-else class="corporate-dialog__hint">
                                {{ t.structure.parentHintSector }}
                            </p>
                        </div>
                    </section>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="moveOpen = false">
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        @click="saveMove"
                    >
                        {{ t.common.confirm }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import OrgTree from '@/components/Admin/Corporate/OrgTree.vue';
import OrgUnitForm from '@/components/Admin/Corporate/OrgUnitForm.vue';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import usersApi from '@/utils/corporate/users';
import { canManageCorporateStructure } from '@/utils/corporate/permissions';
import {
    supportsOrganizationalManagerFeatures,
} from '@/utils/corporate/environment';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';

export default {
    name: 'CorporateOrganizationalStructure',
    mixins: [corporateI18n],
    components: { TitlePage, Loader, OrgTree, OrgUnitForm },
    data() {
        return {
            loading: true,
            loadingList: false,
            error: false,
            saving: false,
            viewMode: 'tree',
            search: '',
            typeFilter: null,
            tree: [],
            units: [],
            listUnits: [],
            listLoaded: false,
            formOpen: false,
            formInitial: {},
            lockType: false,
            moveOpen: false,
            moveTarget: null,
            moveParentId: null,
            managerOptions: [],
            managersLoading: false,
            expandedIds: {},
        };
    },
    computed: {
        ...mapState(['token', 'dark']),
        canManage() {
            return canManageCorporateStructure();
        },
        supportsManager() {
            return supportsOrganizationalManagerFeatures();
        },
        pageSubtitle() {
            return this.supportsManager ? this.t.structure.subtitle : this.t.structure.subtitleApi;
        },
        treeLabels() {
            return {
                ...this.t.structure,
                types: this.t.structure.types,
            };
        },
        formTitle() {
            return this.formInitial.id
                ? this.t.structure.formTitleEdit
                : this.t.structure.formTitleCreate;
        },
        /** Fonte plana para KPIs/forms — sempre derivada da árvore. */
        flatUnits() {
            return this.flattenTree(this.tree);
        },
        counts() {
            const units = this.flatUnits.filter((u) => u.type !== 'company' && u.type !== 'board');
            return {
                sectors: units.filter((u) => u.type === 'sector').length,
                roles: units.filter((u) => u.type === 'role').length,
                withManager: units.filter((u) => u.type === 'sector' && u.managerUserId).length,
                active: units.filter((u) => u.status === 'active').length,
            };
        },
        typeFilterOptions() {
            return [
                { text: this.t.structure.filterAll, value: null },
                { text: this.t.structure.types.sector, value: 'sector' },
                { text: this.t.structure.types.role, value: 'role' },
            ];
        },
        parentOptions() {
            return [
                { text: '—', value: null, unitType: null },
                ...this.flatUnits
                    .filter((u) => u.type === 'company' || u.type === 'sector')
                    .map((u) => ({
                        text: `${this.t.structure.types[u.type]}: ${u.name}`,
                        value: u.id,
                        unitType: u.type,
                    })),
            ];
        },
        moveParentOptions() {
            if (!this.moveTarget) return this.parentOptions;
            const type = this.moveTarget.type;
            let options = this.parentOptions.filter((o) => o.value !== this.moveTarget.id);
            if (type === 'sector') {
                options = options.filter((o) => o.unitType === 'company');
            } else if (type === 'role') {
                options = options.filter((o) => o.unitType === 'sector');
            }
            return options;
        },
        listSource() {
            return this.listLoaded ? this.listUnits : this.flatUnits;
        },
        filteredList() {
            return this.listSource
                .filter((u) => u.type === 'sector' || u.type === 'role')
                .filter((u) => this.matchesFilters(u))
                .sort((a, b) => {
                    const order = { sector: 0, role: 1 };
                    return (order[a.type] ?? 9) - (order[b.type] ?? 9) || a.name.localeCompare(b.name);
                });
        },
        filteredTree() {
            return this.filterTree(this.tree);
        },
    },
    async created() {
        await Promise.all([
            this.loadTree(),
            this.loadAllCollaborators(),
        ]);
    },
    methods: {
        apiErrorMessage(error) {
            return error?.response?.data?.message
                || error?.response?.data?.error
                || error?.message
                || this.t.common.error;
        },
        onFormValidationError(message) {
            alert(message || this.t.common.error);
        },
        flattenTree(nodes, acc = []) {
            (nodes || []).forEach((node) => {
                const { children, ...rest } = node;
                acc.push(rest);
                if (children?.length) {
                    this.flattenTree(children, acc);
                }
            });
            return acc;
        },
        onViewModeChange(mode) {
            if (mode === 'list') {
                this.ensureListLoaded();
            }
        },
        async loadTree() {
            this.loading = true;
            this.error = false;
            try {
                const treeRes = await organizationalUnitsApi.tree(this.token);
                this.tree = treeRes.content || [];
                this.units = this.flattenTree(this.tree);
                this.listLoaded = false;
                this.listUnits = [];
                this.ensureExpandedDefaults(this.tree);
                if (this.viewMode === 'list') {
                    await this.ensureListLoaded(true);
                }
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        async ensureListLoaded(force = false) {
            if (this.listLoaded && !force) return;
            if (this.loadingList) return;
            this.loadingList = true;
            try {
                const listRes = await organizationalUnitsApi.list(this.token);
                this.listUnits = listRes.content || [];
                this.listLoaded = true;
            } catch (e) {
                console.error(e);
                // Fallback: mostra flatten da árvore já carregada
                this.listUnits = this.flatUnits;
                this.listLoaded = true;
                alert(this.apiErrorMessage(e));
            } finally {
                this.loadingList = false;
            }
        },
        /** Mantém compatibilidade com callers que recarregam após mutação. */
        async loadAll() {
            await this.loadTree();
        },
        ensureExpandedDefaults(nodes) {
            nodes.forEach((node) => {
                if (this.expandedIds[node.id] === undefined) {
                    this.expandedIds[node.id] = true;
                }
                if (node.children?.length) {
                    this.ensureExpandedDefaults(node.children);
                }
            });
        },
        toggleExpand(id) {
            this.expandedIds = {
                ...this.expandedIds,
                [id]: this.expandedIds[id] === false,
            };
        },
        matchesFilters(unit) {
            if (this.typeFilter && unit.type !== this.typeFilter) return false;
            if (!this.search?.trim()) return true;
            const term = this.search.trim().toLowerCase();
            return (
                unit.name?.toLowerCase().includes(term)
                || unit.description?.toLowerCase().includes(term)
                || (this.supportsManager && unit.managerName?.toLowerCase().includes(term))
            );
        },
        filterTree(nodes) {
            return (nodes || [])
                .map((node) => {
                    const children = this.filterTree(node.children || []);
                    const selfMatch = this.matchesFilters(node);
                    if (!selfMatch && !children.length) return null;
                    if (this.typeFilter && node.type !== this.typeFilter && !children.length) return null;
                    return { ...node, children };
                })
                .filter(Boolean);
        },
        parentName(parentId) {
            if (!parentId) return '—';
            const parent = this.flatUnits.find((u) => u.id === parentId)
                || this.listUnits.find((u) => u.id === parentId);
            return parent?.name || '—';
        },
        openUnitDetail(unit) {
            if (!unit?.id) return;
            if (unit.type === 'sector') {
                this.$router.push(`/dashboard/corporativo/setores/${unit.id}`);
                return;
            }
            if (unit.type === 'role') {
                this.$router.push({
                    path: `/dashboard/corporativo/cargos/${unit.id}`,
                    query: { from: 'estrutura' },
                });
            }
        },
        defaultParentFor(type) {
            const root = this.flatUnits.find((u) => u.type === 'company');
            if (type === 'sector') {
                return root?.id || null;
            }
            if (type === 'role') {
                const sector = this.flatUnits.find((u) => u.type === 'sector' && u.status === 'active');
                return sector?.id || null;
            }
            return null;
        },
        userToManagerOption(user) {
            const name = user?.name || user?.email || String(user?.id || '');
            const email = user?.email || '';
            return {
                title: email ? `${name} — ${email}` : name,
                name,
                email,
                value: String(user.id),
            };
        },
        ensureManagerInOptions(managerUserId, managerName = '') {
            if (!managerUserId) return;
            const id = String(managerUserId);
            if (this.managerOptions.some((opt) => String(opt.value) === id)) return;
            const name = managerName || id;
            this.managerOptions = [
                {
                    title: name,
                    name,
                    email: '',
                    value: id,
                },
                ...this.managerOptions,
            ];
        },
        async loadAllCollaborators() {
            if (this.managersLoading) return;
            this.managersLoading = true;
            try {
                const users = await usersApi.listAll(this.token);
                this.managerOptions = (users || [])
                    .filter((user) => user?.id)
                    .map((user) => this.userToManagerOption(user));
            } catch (e) {
                console.error(e);
            } finally {
                this.managersLoading = false;
            }
        },
        openCreate(type) {
            this.lockType = true;
            this.formInitial = {
                type,
                parentId: this.defaultParentFor(type),
                name: '',
                description: '',
                managerUserId: null,
                status: 'active',
            };
            this.formOpen = true;
        },
        onAddChild({ parent, type }) {
            this.lockType = true;
            this.formInitial = {
                type,
                parentId: parent.id,
                name: '',
                description: '',
                managerUserId: null,
                status: 'active',
            };
            this.formOpen = true;
        },
        openEdit(node) {
            const managerUserId = node.managerUserId != null && node.managerUserId !== ''
                ? String(node.managerUserId)
                : null;
            this.ensureManagerInOptions(managerUserId, node.managerName);
            this.lockType = true;
            this.formInitial = {
                id: node.id,
                type: node.type,
                parentId: node.parentId,
                name: node.name,
                description: node.description,
                managerUserId,
                status: node.status,
            };
            this.formOpen = true;
        },
        openMove(node) {
            this.moveTarget = node;
            this.moveParentId = node.parentId;
            this.moveOpen = true;
        },
        async onReorder({ parentId, orderedIds }) {
            if (!orderedIds?.length) return;
            try {
                for (let order = 0; order < orderedIds.length; order += 1) {
                    await organizationalUnitsApi.move(this.token, orderedIds[order], {
                        parentId,
                        order,
                    });
                }
                await this.loadAll();
                toast.success(this.t.structure.orderUpdated);
            } catch (e) {
                toast.error(this.apiErrorMessage(e));
                await this.loadAll();
            }
        },
        async saveUnit(payload) {
            this.saving = true;
            try {
                const body = { ...payload };
                if (!this.supportsManager) {
                    delete body.managerUserId;
                }
                if (payload.id) {
                    await organizationalUnitsApi.update(this.token, payload.id, body);
                } else {
                    await organizationalUnitsApi.create(this.token, body);
                }
                this.formOpen = false;
                await this.loadAll();
            } catch (e) {
                alert(this.apiErrorMessage(e));
            } finally {
                this.saving = false;
            }
        },
        async saveMove() {
            if (!this.moveTarget) return;
            this.saving = true;
            try {
                await organizationalUnitsApi.move(this.token, this.moveTarget.id, {
                    parentId: this.moveParentId,
                });
                this.moveOpen = false;
                await this.loadAll();
            } catch (e) {
                alert(this.apiErrorMessage(e));
            } finally {
                this.saving = false;
            }
        },
        async toggleStatus(node) {
            const status = node.status === 'active' ? 'inactive' : 'active';
            try {
                await organizationalUnitsApi.updateStatus(this.token, node.id, status);
                await this.loadAll();
            } catch (e) {
                alert(this.apiErrorMessage(e));
            }
        },
        async confirmRemove(node) {
            if (!window.confirm(this.t.structure.deleteConfirm)) return;
            try {
                await organizationalUnitsApi.remove(this.token, node.id);
                await this.loadAll();
            } catch (e) {
                alert(this.apiErrorMessage(e));
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-structure-kpi {
    display: flex;
    align-items: center;
    gap: 0.85rem;
}

.corporate-structure-kpi__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: #eef0fc;
    color: #250891;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.corporate-structure-kpi__icon--sector {
    background: rgba(57, 36, 151, 0.12);
    color: #392497;
}

.corporate-structure-kpi__icon--role {
    background: rgba(26, 199, 208, 0.15);
    color: #0e8f96;
}

.corporate-structure-toolbar__toggle {
    margin-left: auto;
}

.corporate-structure-table-wrap {
    overflow-x: auto;
}

.corporate-structure-list-name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
}

.corporate-structure-list-link {
    border: 0;
    background: transparent;
    padding: 0;
    font: inherit;
    font-weight: 700;
    color: #250891;
    cursor: pointer;
    text-align: left;
}

.corporate-structure-list-link:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.corporate-structure-list-badge {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.4rem;
    border-radius: 999px;
    background: #eef0fc;
    color: #250891;
}

.corporate-structure-list-badge--sector {
    background: rgba(57, 36, 151, 0.12);
    color: #392497;
}

.corporate-structure-list-badge--role {
    background: rgba(26, 199, 208, 0.16);
    color: #0e8f96;
}

.corporate-structure-list-desc {
    display: block;
    margin-top: 0.2rem;
    color: #8a8f9c;
}

.corporate-structure-status {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
}

.corporate-structure-status.is-on {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.corporate-structure-status.is-off {
    background: rgba(198, 40, 40, 0.1);
    color: #c62828;
}

@media (max-width: 767px) {
    .corporate-structure-toolbar__toggle {
        margin-left: 0;
        width: 100%;
    }
}

.dashboard.dark .corporate-structure-kpi__icon {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-structure-kpi__icon--sector {
    background: rgba(165, 180, 252, 0.15);
    color: #a5b4fc;
}

.dashboard.dark .corporate-structure-kpi__icon--role {
    background: rgba(26, 199, 208, 0.18);
    color: #5eead4;
}

.dashboard.dark .corporate-structure-list-link {
    color: #c4b5fd;
}

.dashboard.dark .corporate-structure-list-badge {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-structure-list-badge--sector {
    background: rgba(165, 180, 252, 0.15);
    color: #a5b4fc;
}

.dashboard.dark .corporate-structure-list-badge--role {
    background: rgba(26, 199, 208, 0.18);
    color: #5eead4;
}

.dashboard.dark .corporate-structure-list-desc {
    color: #9ca3b4;
}

.dashboard.dark .corporate-structure-status.is-on {
    background: rgba(46, 125, 50, 0.22);
    color: #86efac;
}

.dashboard.dark .corporate-structure-status.is-off {
    background: rgba(198, 40, 40, 0.2);
    color: #fca5a5;
}
</style>
